import React from "react";
import { Card, Row, Col, Typography, Empty, Table, Tag, Progress, Tooltip } from "antd";
import {
  UserOutlined,
  TeamOutlined,
  CrownOutlined,
  StarOutlined,
  RocketOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { motion } from "framer-motion";

const { Text, Title } = Typography;

const AUDIENCE_COLORS = {
  all_users: "#1890ff",
  free_users: "#8c8c8c",
  trial_users: "#faad14",
  paid_users: "#52c41a",
  churned_users: "#ff4d4f",
  new_users: "#13c2c2",
  returning_users: "#722ed1",
  vip_users: "#eb2f96",
};

const AUDIENCE_ICONS = {
  all_users: <TeamOutlined />,
  free_users: <UserOutlined />,
  trial_users: <ClockCircleOutlined />,
  paid_users: <CrownOutlined />,
  churned_users: <UserOutlined />,
  new_users: <StarOutlined />,
  returning_users: <RocketOutlined />,
  vip_users: <CrownOutlined />,
};

const AudienceBreakdown = ({
  title = "Audience Performance",
  data = {},
  loading = false,
  style = {},
}) => {
  const { segments = [], summary = {} } = data;

  // Format segment name for display
  const formatSegmentName = (name) => {
    return name
      ?.replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Get color for segment
  const getSegmentColor = (segment) => {
    return AUDIENCE_COLORS[segment?.toLowerCase()] || "#1890ff";
  };

  // Get icon for segment
  const getSegmentIcon = (segment) => {
    return AUDIENCE_ICONS[segment?.toLowerCase()] || <UserOutlined />;
  };

  // Transform segments for chart
  const chartData = segments.map((segment) => ({
    name: formatSegmentName(segment.segment),
    views: segment.views || 0,
    clicks: segment.clicks || 0,
    conversions: segment.conversions || 0,
    ctr: segment.ctr || 0,
    conversionRate: segment.conversionRate || 0,
    segment: segment.segment,
  }));

  // Table columns
  const columns = [
    {
      title: "Segment",
      dataIndex: "segment",
      key: "segment",
      render: (segment) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <span
            style={{
              color: getSegmentColor(segment),
              marginRight: 8,
              fontSize: 16,
            }}
          >
            {getSegmentIcon(segment)}
          </span>
          <Tag color={getSegmentColor(segment)}>
            {formatSegmentName(segment)}
          </Tag>
        </div>
      ),
    },
    {
      title: "Views",
      dataIndex: "views",
      key: "views",
      sorter: (a, b) => a.views - b.views,
      render: (views) => views?.toLocaleString() || 0,
    },
    {
      title: "Clicks",
      dataIndex: "clicks",
      key: "clicks",
      sorter: (a, b) => a.clicks - b.clicks,
      render: (clicks) => clicks?.toLocaleString() || 0,
    },
    {
      title: "CTR",
      dataIndex: "ctr",
      key: "ctr",
      sorter: (a, b) => a.ctr - b.ctr,
      render: (ctr) => (
        <Tooltip title={`Click-through rate: ${ctr?.toFixed(2)}%`}>
          <Progress
            percent={Math.min(ctr || 0, 100)}
            size="small"
            strokeColor={ctr > 5 ? "#52c41a" : ctr > 2 ? "#faad14" : "#ff4d4f"}
            format={() => `${ctr?.toFixed(1)}%`}
          />
        </Tooltip>
      ),
    },
    {
      title: "Conversions",
      dataIndex: "conversions",
      key: "conversions",
      sorter: (a, b) => a.conversions - b.conversions,
      render: (conversions) => (
        <Tag color={conversions > 0 ? "green" : "default"}>
          {conversions?.toLocaleString() || 0}
        </Tag>
      ),
    },
    {
      title: "Conv. Rate",
      dataIndex: "conversionRate",
      key: "conversionRate",
      sorter: (a, b) => a.conversionRate - b.conversionRate,
      render: (rate) => (
        <Tag color={rate > 5 ? "green" : rate > 2 ? "orange" : "default"}>
          {rate?.toFixed(2)}%
        </Tag>
      ),
    },
  ];

  // Custom tooltip for bar chart
  const CustomBarTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div
        style={{
          background: "white",
          padding: "12px 16px",
          border: "1px solid #f0f0f0",
          borderRadius: 8,
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        }}
      >
        <Text strong style={{ display: "block", marginBottom: 8 }}>
          {label}
        </Text>
        {payload.map((entry, index) => (
          <div key={index} style={{ marginTop: 4 }}>
            <span
              style={{
                display: "inline-block",
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: entry.fill,
                marginRight: 8,
              }}
            />
            <Text type="secondary">{entry.name}: </Text>
            <Text strong>{entry.value?.toLocaleString()}</Text>
          </div>
        ))}
      </div>
    );
  };

  if (!segments || segments.length === 0) {
    return (
      <Card title={title} bordered={false} loading={loading} style={style}>
        <Empty
          description="No audience data available"
          style={{ padding: "40px 0" }}
        />
      </Card>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card title={title} bordered={false} loading={loading} style={style}>
        {/* Summary Stats */}
        {summary && Object.keys(summary).length > 0 && (
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={12} sm={6}>
              <Card
                size="small"
                style={{ background: "#e6f7ff", textAlign: "center" }}
              >
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Total Segments
                </Text>
                <div style={{ fontSize: 20, fontWeight: 600, color: "#1890ff" }}>
                  {segments.length}
                </div>
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card
                size="small"
                style={{ background: "#f6ffed", textAlign: "center" }}
              >
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Best CTR
                </Text>
                <div style={{ fontSize: 20, fontWeight: 600, color: "#52c41a" }}>
                  {Math.max(...segments.map((s) => s.ctr || 0)).toFixed(1)}%
                </div>
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card
                size="small"
                style={{ background: "#f9f0ff", textAlign: "center" }}
              >
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Top Segment
                </Text>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#722ed1",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {formatSegmentName(
                    segments.sort((a, b) => (b.conversions || 0) - (a.conversions || 0))[0]
                      ?.segment
                  ) || "N/A"}
                </div>
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card
                size="small"
                style={{ background: "#fff7e6", textAlign: "center" }}
              >
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Avg. Conv. Rate
                </Text>
                <div style={{ fontSize: 20, fontWeight: 600, color: "#fa8c16" }}>
                  {(
                    segments.reduce((sum, s) => sum + (s.conversionRate || 0), 0) /
                    segments.length
                  ).toFixed(1)}
                  %
                </div>
              </Card>
            </Col>
          </Row>
        )}

        {/* Bar Chart Comparison */}
        <Card
          title="Segment Comparison"
          size="small"
          bordered={false}
          style={{ background: "#fafafa", marginBottom: 24 }}
        >
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11 }}
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis tick={{ fontSize: 11 }} />
                <RechartsTooltip content={<CustomBarTooltip />} />
                <Bar
                  dataKey="views"
                  name="Views"
                  fill="#1890ff"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={40}
                />
                <Bar
                  dataKey="clicks"
                  name="Clicks"
                  fill="#52c41a"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={40}
                />
                <Bar
                  dataKey="conversions"
                  name="Conversions"
                  fill="#722ed1"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={40}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Detailed Table */}
        <Title level={5}>Segment Details</Title>
        <Table
          columns={columns}
          dataSource={chartData}
          pagination={false}
          rowKey={(record) => record.segment}
          size="middle"
          scroll={{ x: "max-content" }}
        />
      </Card>
    </motion.div>
  );
};

export default AudienceBreakdown;
